import { defineComponent as _defineComponent } from 'vue'
import { createElementVNode as _createElementVNode, toDisplayString as _toDisplayString, openBlock as _openBlock, createElementBlock as _createElementBlock, createCommentVNode as _createCommentVNode, createTextVNode as _createTextVNode, unref as _unref, withCtx as _withCtx, createVNode as _createVNode, normalizeClass as _normalizeClass, renderList as _renderList, Fragment as _Fragment, createStaticVNode as _createStaticVNode } from "vue"

const _hoisted_1 = { class: "room-state" }
const _hoisted_2 = { class: "rs-header" }
const _hoisted_3 = { class: "rs-tools" }
const _hoisted_4 = { class: "rs-count" }
const _hoisted_5 = {
  key: 0,
  class: "rs-updated"
}
const _hoisted_6 = {
  key: 0,
  class: "rs-op-error"
}
const _hoisted_7 = { class: "rs-stats" }
const _hoisted_8 = { class: "stat-num" }
const _hoisted_9 = { class: "stat-num" }
const _hoisted_10 = { class: "stat-num" }
const _hoisted_11 = { class: "stat-num" }
const _hoisted_12 = {
  key: 1,
  class: "rs-detail"
}
const _hoisted_13 = { class: "detail-block" }
const _hoisted_14 = { class: "detail-name" }
const _hoisted_15 = { class: "detail-chips" }
const _hoisted_16 = {
  key: 0,
  class: "chip-name"
}
const _hoisted_17 = { class: "rs-table" }
const _hoisted_18 = { class: "rs-body" }
const _hoisted_19 = ["onClick"]
const _hoisted_20 = { class: "g-arrow" }
const _hoisted_21 = { class: "g-name" }
const _hoisted_22 = { class: "g-count" }
const _hoisted_23 = { class: "c-room" }
const _hoisted_24 = { class: "room-key" }
const _hoisted_25 = {
  key: 0,
  class: "room-name"
}
const _hoisted_26 = ["title"]
const _hoisted_27 = { class: "d-name" }
const _hoisted_28 = ["title"]
const _hoisted_29 = { class: "c-status" }
const _hoisted_30 = { class: "c-kill" }
const _hoisted_31 = { class: "c-time" }
const _hoisted_32 = {
  key: 0,
  class: "rs-empty"
}
const _hoisted_33 = {
  key: 1,
  class: "rs-empty"
}

import SignalRClient from '@/ts/SignalRClient';
import RoomStateInfo from '@/ts/VO/RoomStateInfo';
import { NButton, NSwitch } from 'naive-ui';
import { ref, computed, onMounted, onUnmounted } from 'vue';

type StatFilter = 'all' | 'maintaining' | 'disabled' | 'dup';

export default /*@__PURE__*/_defineComponent({
  __name: 'RoomStateView',
  emits: ["roomKillPigChanged"],
  setup(__props, { emit: __emit }) {

const rooms = ref<RoomStateInfo[]>([]);
const loading = ref(false);
const lastRefresh = ref<string>('');
let timer: number | null = null;

/** 单房间托管开关变更（成功后通知父页面同步 selectRooms 等状态） */
const emit = __emit;

/** 正在切换托管的房间（防抖，开关转圈直到接口返回） */
const toggling = ref(new Set<string>());
/** 行内错误提示（项目未挂 n-message-provider，不能用 useMessage） */
const opError = ref('');
let opErrorTimer: number | null = null;
function showOpError(msg: string) {
  opError.value = msg;
  if (opErrorTimer != null) window.clearTimeout(opErrorTimer);
  opErrorTimer = window.setTimeout(() => { opError.value = ''; }, 4000);
}

async function toggleKillPig(r: RoomStateInfo, open: boolean) {
  if (r.IsMaintaining) return; // 维护中房间不可操作托管
  if (toggling.value.has(r.RoomNumber ?? '')) return;
  toggling.value.add(r.RoomNumber ?? '');
  toggling.value = new Set(toggling.value); // 触发响应式
  try {
    const ok = await SignalRClient.Instance.InvokeAsync<boolean>('SetRoomOpenKillPig', r.GameType, r.GameCode, open);
    if (ok !== true) {
      // 服务端没落地（房间已不存在/异常被 catch）：不要乐观更新，也不要通知父页写基线
      showOpError(`切换 ${r.RoomNumber ?? ''} 托管失败：房间不存在或服务端异常`);
      await refresh();
      return;
    }
    r.OpenKillPig = open; // 乐观更新，5s 轮询会再以服务端为准
    emit('roomKillPigChanged', r.RoomNumber ?? '', open);
  } catch {
    showOpError(`切换 ${r.RoomNumber ?? ''} 托管失败：连接异常`);
    await refresh();
  } finally {
    toggling.value.delete(r.RoomNumber ?? '');
    toggling.value = new Set(toggling.value);
  }
}

/** 统计卡筛选：all / maintaining / disabled / dup */
const filter = ref<StatFilter>('all');
function setFilter(f: StatFilter) {
  // 再点一次当前筛选 = 取消，回到全部
  filter.value = filter.value === f ? 'all' : f;
}

async function refresh() {
  loading.value = true;
  try {
    const list = await SignalRClient.Instance.InvokeAsync<RoomStateInfo[]>("GetRoomStates");
    rooms.value = list ?? [];
    lastRefresh.value = fmtNow();
  } catch {
    /* 静默失败，下次刷新重试 */
  } finally {
    loading.value = false;
  }
}

const maintainingRooms = computed(() => rooms.value.filter((r) => r.IsMaintaining));
const disabledRooms = computed(() => rooms.value.filter((r) => !r.OpenKillPig));

// 主播重名：同一房间类型(GameType，如 3001/3047)、同一系列（桌名前缀字母，如 MD/EU/AS，兼容任意前缀）内、
// 同一个主播出现在 ≥2 个受控房间。与后端 GetRoomStates / CountRoomsWithDealer 口径一致：
// 双方都有 did ⇒ 比 did（同名不同人不会误报）；任一方缺 did ⇒ 回退比 dn。
function seriesOf(roomName?: string): string {
  // 与后端 VirtualGameInfoFactoryService.GetRoomSeries 的 char.IsLetter 对齐：
  // 取开头连续 Unicode 字母（中文桌名也算前缀），统一大写。
  const m = (roomName ?? '').trim().match(/^\p{L}+/u);
  return m ? m[0].toUpperCase() : '';
}
function sameDealer(a: RoomStateInfo, b: RoomStateInfo): boolean {
  const ai = (a.DealerId ?? '').trim();
  const bi = (b.DealerId ?? '').trim();
  if (ai && bi) return ai === bi;
  const an = (a.DealerName ?? '').trim();
  const bn = (b.DealerName ?? '').trim();
  return !!an && an === bn;
}
const duplicateGroups = computed(() => {
  // 先按 房间类型+系列 分桶，再在桶内按“同一个主播”聚类（两两比较，兼容 did 有的有、有的没）
  const buckets = new Map<string, RoomStateInfo[]>();
  for (const r of rooms.value) {
    if (r.IsMaintaining) continue; // 维护中的房间荷官可能为残留，不参与重名对比
    if (!(r.DealerId ?? '').trim() && !(r.DealerName ?? '').trim()) continue;
    const key = (r.GameType ?? '') + '\u0001' + seriesOf(r.RoomName);
    if (!buckets.has(key)) buckets.set(key, []);
    buckets.get(key)!.push(r);
  }

  const groups: { key: string; name: string; rooms: { no: string; name: string }[] }[] = [];
  for (const [bucketKey, list] of buckets) {
    const clusters: RoomStateInfo[][] = [];
    for (const r of list) {
      const hit = clusters.find((c) => sameDealer(c[0], r));
      if (hit) hit.push(r);
      else clusters.push([r]);
    }
    for (const c of clusters) {
      if (c.length < 2) continue;
      const dn = (c[0].DealerName ?? '').trim();
      const did = (c[0].DealerId ?? '').trim();
      const label = dn || (did ? `ID ${did}` : '—');
      groups.push({
        key: bucketKey + '\u0001' + (did || dn),
        // 有 ID 时一并展示，便于核对“同名到底是不是同一个人”
        name: did ? `${label}（ID:${did}）` : label,
        rooms: c.map((x) => ({ no: x.RoomNumber ?? '', name: x.RoomName ?? '' })),
      });
    }
  }
  return groups;
});
const duplicateRooms = computed(() => rooms.value.filter((r) => r.IsDuplicateDealer));

const shownRooms = computed(() => {
  let list: RoomStateInfo[];
  switch (filter.value) {
    case 'maintaining': list = maintainingRooms.value; break;
    case 'disabled': list = disabledRooms.value; break;
    case 'dup': list = duplicateRooms.value; break;
    default: list = rooms.value;
  }
  return [...list].sort((a, b) => {
    // 重名房间排最前（显眼），其余按房间号
    const da = a.IsDuplicateDealer ? 1 : 0;
    const db = b.IsDuplicateDealer ? 1 : 0;
    if (da !== db) return db - da;
    return (a.RoomNumber ?? '').localeCompare(b.RoomNumber ?? '');
  });
});

/** 按房间类型(3001/3047)+房间头（桌名前缀，如 MD/EU/AS；无前缀归为“未分组”）分组；组内房间已按房间号排序 */
const shownGroups = computed(() => {
  const groups = new Map<string, { gameType: string; series: string; rooms: RoomStateInfo[] }>();
  for (const r of shownRooms.value) {
    const gt = r.GameType ?? '';
    const s = seriesOf(r.RoomName);
    const key = gt + '\u0001' + s;
    if (!groups.has(key)) groups.set(key, { gameType: gt, series: s, rooms: [] });
    groups.get(key)!.rooms.push(r);
  }
  return [...groups.values()].sort((a, b) => {
    if (a.gameType !== b.gameType) return a.gameType.localeCompare(b.gameType);
    const sa = a.series, sb = b.series;
    if (sa === '' && sb !== '') return 1;  // 未分组的排最后
    if (sb === '' && sa !== '') return -1;
    return sa.localeCompare(sb);
  }).map(g => ({
    key: g.gameType + '\u0001' + g.series,
    label: (g.series || '未分组') + (g.gameType ? ` · ${g.gameType}` : ''),
    rooms: g.rooms,
  }));
});

/** 折叠的分组（点击组头切换） */
const collapsed = ref(new Set<string>());
function toggleGroup(s: string) {
  const n = new Set(collapsed.value);
  if (n.has(s)) n.delete(s); else n.add(s);
  collapsed.value = n;
}

function pad(n: number): string {
  return n < 10 ? '0' + n : '' + n;
}
function fmtTime(iso?: string): string {
  if (!iso) return '未更新';
  const d = new Date(iso);
  if (isNaN(d.getTime()) || d.getFullYear() <= 1) return '未更新';
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())} ${pad(d.getHours())}:${pad(
    d.getMinutes()
  )}:${pad(d.getSeconds())}`;
}
function fmtNow(): string {
  return fmtTime(new Date().toISOString());
}

function startPoll() {
  refresh();
  timer = window.setInterval(refresh, 5000);
}
function stopPoll() {
  if (timer != null) {
    window.clearInterval(timer);
    timer = null;
  }
}

// 重连后兜底刷新一次
const onReconnect = () => refresh();

onMounted(() => {
  startPoll();
  SignalRClient.Instance.AddReconnectListener(onReconnect);
});
onUnmounted(() => {
  stopPoll();
  if (opErrorTimer != null) {
    window.clearTimeout(opErrorTimer);
    opErrorTimer = null;
  }
  SignalRClient.Instance.RemoveReconnectListener(onReconnect);
});

return (_ctx: any,_cache: any) => {
  return (_openBlock(), _createElementBlock("div", _hoisted_1, [
    _createElementVNode("div", _hoisted_2, [
      _cache[5] || (_cache[5] = _createElementVNode("div", { class: "rs-title" }, "受控房间状态", -1)),
      _createElementVNode("div", _hoisted_3, [
        _createElementVNode("span", _hoisted_4, "共 " + _toDisplayString(rooms.value.length) + " 个", 1),
        (lastRefresh.value)
          ? (_openBlock(), _createElementBlock("span", _hoisted_5, "刷新于 " + _toDisplayString(lastRefresh.value), 1))
          : _createCommentVNode("", true),
        _createVNode(_unref(NButton), {
          size: "small",
          quaternary: "",
          loading: loading.value,
          onClick: refresh
        }, {
          default: _withCtx(() => _cache[4] || (_cache[4] = [
            _createTextVNode("刷新")
          ])),
          _: 1
        }, 8, ["loading"])
      ])
    ]),
    (opError.value)
      ? (_openBlock(), _createElementBlock("div", _hoisted_6, _toDisplayString(opError.value), 1))
      : _createCommentVNode("", true),
    _createElementVNode("div", _hoisted_7, [
      _createElementVNode("div", {
        class: _normalizeClass(["stat-card clickable", { active: filter.value === 'all' }]),
        onClick: _cache[0] || (_cache[0] = ($event: any) => (setFilter('all')))
      }, [
        _createElementVNode("div", _hoisted_8, _toDisplayString(rooms.value.length), 1),
        _cache[6] || (_cache[6] = _createElementVNode("div", { class: "stat-label" }, "总房间", -1))
      ], 2),
      _createElementVNode("div", {
        class: _normalizeClass(["stat-card clickable", { active: filter.value === 'maintaining', 'has-maint': maintainingRooms.value.length }]),
        onClick: _cache[1] || (_cache[1] = ($event: any) => (setFilter('maintaining')))
      }, [
        _createElementVNode("div", _hoisted_9, _toDisplayString(maintainingRooms.value.length), 1),
        _cache[7] || (_cache[7] = _createElementVNode("div", { class: "stat-label" }, "维护中", -1))
      ], 2),
      _createElementVNode("div", {
        class: _normalizeClass(["stat-card clickable", { active: filter.value === 'disabled' }]),
        onClick: _cache[2] || (_cache[2] = ($event: any) => (setFilter('disabled')))
      }, [
        _createElementVNode("div", _hoisted_10, _toDisplayString(disabledRooms.value.length), 1),
        _cache[8] || (_cache[8] = _createElementVNode("div", { class: "stat-label" }, "托管关闭", -1))
      ], 2),
      _createElementVNode("div", {
        class: _normalizeClass(["stat-card clickable", { active: filter.value === 'dup', 'has-danger': duplicateRooms.value.length }]),
        onClick: _cache[3] || (_cache[3] = ($event: any) => (setFilter('dup')))
      }, [
        _createElementVNode("div", _hoisted_11, _toDisplayString(duplicateGroups.value.length), 1),
        _cache[9] || (_cache[9] = _createElementVNode("div", { class: "stat-label" }, "主播重名组", -1))
      ], 2)
    ]),
    (duplicateGroups.value.length)
      ? (_openBlock(), _createElementBlock("div", _hoisted_12, [
          _createElementVNode("div", _hoisted_13, [
            _cache[10] || (_cache[10] = _createElementVNode("div", { class: "detail-title danger" }, "主播重名（同一主播同时出现在多张牌台）", -1)),
            (_openBlock(true), _createElementBlock(_Fragment, null, _renderList(duplicateGroups.value, (g) => {
              return (_openBlock(), _createElementBlock("div", {
                class: "detail-row",
                key: g.key
              }, [
                _createElementVNode("span", _hoisted_14, _toDisplayString(g.name), 1),
                _createElementVNode("span", _hoisted_15, [
                  (_openBlock(true), _createElementBlock(_Fragment, null, _renderList(g.rooms, (room) => {
                    return (_openBlock(), _createElementBlock("span", {
                      class: "chip",
                      key: room.no
                    }, [
                      _createTextVNode(_toDisplayString(room.no), 1),
                      (room.name)
                        ? (_openBlock(), _createElementBlock("i", _hoisted_16, _toDisplayString(room.name), 1))
                        : _createCommentVNode("", true)
                    ]))
                  }), 128))
                ])
              ]))
            }), 128))
          ])
        ]))
      : _createCommentVNode("", true),
    _createElementVNode("div", _hoisted_17, [
      _cache[11] || (_cache[11] = _createStaticVNode("<div class=\"rs-row rs-head\" data-v-041ef326><span class=\"c-room\" data-v-041ef326>房间</span><span class=\"c-dealer\" data-v-041ef326>主播</span><span class=\"c-status\" data-v-041ef326>状态</span><span class=\"c-kill\" data-v-041ef326>托管</span><span class=\"c-time\" title=\"该房间最近一次收到上游数据包的时间，每个房间独立（开局/发牌节奏不同）\" data-v-041ef326>最后数据</span></div>", 1)),
      _createElementVNode("div", _hoisted_18, [
        (_openBlock(true), _createElementBlock(_Fragment, null, _renderList(shownGroups.value, (g) => {
          return (_openBlock(), _createElementBlock("div", {
            class: "rs-group",
            key: g.key
          }, [
            _createElementVNode("div", {
              class: "rs-group-head",
              onClick: ($event: any) => (toggleGroup(g.key))
            }, [
              _createElementVNode("span", _hoisted_20, _toDisplayString(collapsed.value.has(g.key) ? '▸' : '▾'), 1),
              _createElementVNode("span", _hoisted_21, _toDisplayString(g.label), 1),
              _createElementVNode("span", _hoisted_22, _toDisplayString(g.rooms.length), 1)
            ], 8, _hoisted_19),
            (!collapsed.value.has(g.key))
              ? (_openBlock(true), _createElementBlock(_Fragment, { key: 0 }, _renderList(g.rooms, (r) => {
                  return (_openBlock(), _createElementBlock("div", {
                    class: _normalizeClass(["rs-row", { 'row-dup': r.IsDuplicateDealer, 'row-maint': r.IsMaintaining }]),
                    key: r.RoomNumber
                  }, [
                    _createElementVNode("span", _hoisted_23, [
                      _createElementVNode("div", _hoisted_24, _toDisplayString(r.RoomNumber), 1),
                      (r.RoomName)
                        ? (_openBlock(), _createElementBlock("div", _hoisted_25, _toDisplayString(r.RoomName), 1))
                        : _createCommentVNode("", true)
                    ]),
                    _createElementVNode("span", {
                      class: _normalizeClass(["c-dealer", { dup: r.IsDuplicateDealer }]),
                      title: r.IsDuplicateDealer ? '主播重名：该主播同时出现在多张牌台' : undefined
                    }, [
                      _createElementVNode("span", _hoisted_27, _toDisplayString(r.DealerName || '—'), 1),
                      (r.DealerId)
                        ? (_openBlock(), _createElementBlock("span", {
                            key: 0,
                            class: "d-id",
                            title: '主播 ID：' + r.DealerId
                          }, _toDisplayString(r.DealerId), 9, _hoisted_28))
                        : _createCommentVNode("", true)
                    ], 10, _hoisted_26),
                    _createElementVNode("span", _hoisted_29, [
                      _createElementVNode("span", {
                        class: _normalizeClass(["badge", r.IsMaintaining ? 'bad-warn' : 'bad-ok'])
                      }, _toDisplayString(r.IsMaintaining ? '维护中' : '正常'), 3)
                    ]),
                    _createElementVNode("span", _hoisted_30, [
                      _createVNode(_unref(NSwitch), {
                        size: "small",
                        value: !!r.OpenKillPig,
                        loading: toggling.value.has(r.RoomNumber),
                        disabled: r.IsMaintaining,
                        title: r.IsMaintaining ? '维护中不可操作托管' : undefined,
                        "onUpdate:value": (v) => toggleKillPig(r, v)
                      }, null, 8, ["value", "loading", "disabled", "title", "onUpdate:value"])
                    ]),
                    _createElementVNode("span", _hoisted_31, _toDisplayString(fmtTime(r.LastUpdateTime)), 1)
                  ], 2))
                }), 128))
              : _createCommentVNode("", true)
          ]))
        }), 128)),
        (!shownRooms.value.length && !loading.value)
          ? (_openBlock(), _createElementBlock("div", _hoisted_32, _toDisplayString(filter.value === 'all' ? '暂无受控房间' : '该筛选条件下暂无房间'), 1))
          : _createCommentVNode("", true),
        (loading.value && !rooms.value.length)
          ? (_openBlock(), _createElementBlock("div", _hoisted_33, "加载中…"))
          : _createCommentVNode("", true)
      ])
    ])
  ]))
}
}

})